// ReraUploadModal.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import * as pdfjsLib from "pdfjs-dist";
import { createWorker } from "tesseract.js";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const MIN_TEXT_LENGTH = 20;

const extractTextFromPdfFile = async (file) => {
  const data = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data });
  const pdf = await loadingTask.promise;
  let fullText = "";

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const strings = content.items.map((item) => item.str || "");
    fullText += strings.join(" ") + " ";
  }

  return fullText.replace(/\s+/g, " ").trim();
};

// Scanned/photographed PDFs have no embedded text layer, so pdf.js's
// getTextContent() comes back empty. Render each page to a canvas and OCR it.
const extractTextFromScannedPdf = async (file, onProgress) => {
  const data = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data }).promise;
  const worker = await createWorker("eng");

  let fullText = "";
  try {
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 2 });
      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext("2d");
      await page.render({ canvasContext: ctx, viewport }).promise;

      const {
        data: { text },
      } = await worker.recognize(canvas);
      fullText += text + " ";

      if (onProgress) onProgress(i / pdf.numPages);
    }
  } finally {
    await worker.terminate();
  }

  return fullText.replace(/\s+/g, " ").trim();
};

const ReraUploadModal = ({ open, onClose, onExtractData }) => {
  const fileRef = useRef();
  const [error, setError] = useState("");
  const [uploaded, setUploaded] = useState(false);
  const [ocrRunning, setOcrRunning] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);


useEffect(() => {
  if (open) {
    // Reset state every time modal opens
    setUploaded(false);
    setError("");
    setOcrRunning(false);
    setOcrProgress(0);
    if (fileRef.current) fileRef.current.value = null;
  }
}, [open]);

  const handleFile = async (e) => {
    setError("");
    const file = e.target.files?.[0];

    if (!file || file.type !== "application/pdf") {
      setError("Please upload a valid PDF file.");
      setUploaded(false);
      return;
    }

    try {
      let text = await extractTextFromPdfFile(file);

      if (!text || text.length < MIN_TEXT_LENGTH) {
        // No embedded text layer — likely a scanned/photographed certificate.
        // Fall back to OCR by rendering each page and recognizing its text.
        setOcrRunning(true);
        setOcrProgress(0);
        try {
          text = await extractTextFromScannedPdf(file, setOcrProgress);
        } finally {
          setOcrRunning(false);
        }
      }

      if (!text || text.length < MIN_TEXT_LENGTH) {
        setError(
          "The PDF doesn't contain readable text. It might be scanned at too low a quality — please upload a clearer copy."
        );
        setUploaded(false);
        return;
      }

      setUploaded(true); // ✅ mark as uploaded
      onExtractData(text);
        onClose();
    } catch (err) {
      console.error("❌ Failed to read uploaded PDF:", err);
      setError(`Failed to read the PDF: ${err.message}`);
      setUploaded(false);
      setOcrRunning(false);
    }
        // onClose();
  };

  const handleClose = () => {
    if (ocrRunning) return; // ❌ prevent closing mid-OCR
    if (!uploaded) {
      setError("⚠️ Uploading the RERA certificate is mandatory.");
      return; // ❌ prevent closing until uploaded
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle sx={{ fontSize: "1.7rem", fontWeight: "bold" }}>
        Upload RERA Certificate
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2, fontSize: "1.4rem" }}>
          Please upload your RERA Certificate PDF for manual verification.
        </Typography>
        {error && (
          <Typography
            color="error"
            variant="body1"
            sx={{ mb: 1, fontSize: "1rem", fontWeight: 500 }}
          >
            {error}
          </Typography>
        )}
        {ocrRunning && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <CircularProgress size={20} variant={ocrProgress > 0 ? "determinate" : "indeterminate"} value={ocrProgress * 100} />
            <Typography variant="body2">
              Scanned PDF detected — reading text via OCR, this may take a moment...
            </Typography>
          </Box>
        )}
        <Button
          variant="contained"
          sx={{ fontSize: "1.3rem", padding: "10px 20px", mt: 2 }}
          onClick={() => fileRef.current?.click()}
          disabled={ocrRunning}
        >
          Upload PDF
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf"
          hidden
          onChange={handleFile}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ReraUploadModal;